// Frame 13 (33:455) — term detail. The run's designated second action:
// walkthrough beats 6 and 7.
//
// Three things this screen must get right, all of them invisible in a
// screenshot:
//
//  * It reads its term from the URL, so pasting the link into a fresh tab
//    renders the same screen. Nothing here depends on navigation state.
//  * The URL carries `doc` beside the term id, because a `term_id` is
//    doc-local: `t04` names a different term in every document, so a detail
//    fetched without it aggregates a mixture and the four stat tiles stop
//    agreeing with the leaderboard row that was clicked.
//  * An unknown or cross-org id renders a not-found panel. The backend returns
//    404 for both, deliberately, so the two are indistinguishable from here and
//    the copy must not claim to know which it was.

import { Link } from "@tanstack/react-router";
import {
  Badge,
  Card,
  EmptyState,
  ErrorState,
  OriginPill,
  MeterBar,
  PageHeader,
  Skeleton,
  SkeletonStack,
  StatRow,
  StatTile,
} from "../../ui";
import { disclose } from "../../reading/termOrigin";
import type { TermOrigin } from "../../data/types";
import { useTermDetail, type TermDetail, type TermHistoryPoint } from "./api";
import {
  compareByLookups,
  formatCount,
  formatDuration,
  formatLookupRate,
  formatScore,
  formatShare,
  lookupsPerSession,
  medianOf,
  shownOf,
} from "./format";
import {
  LOOKUPS_PER_VISIT_WHY,
  NOT_MEASURED,
  NOT_MEASURED_WHY,
  NO_LOOKUPS_MEASURED,
  NO_LOOKUPS_WHY,
  SECTION_SHARE_LABEL,
} from "./metricLabels";
import "./analytics.css";

const MAX_DOC_ROWS = 6;
const MAX_DOC_CHIPS = 12;

const ORIGINS: readonly string[] = ["openapi", "llm", "user", "extension"];

function isOrigin(value: string | null): value is TermOrigin {
  return value !== null && ORIGINS.includes(value);
}

export type DocLookupRow = {
  doc_id: string;
  hovers: number;
  sessions: number;
  quickExits: number;
  medianDwellMs: number | null;
  sectionShare: number | null;
  /** `null` when no visit opened the term in that document — an absent
   *  denominator, not a rate of zero. Those rows keep their counts and show
   *  NOT_MEASURED in place of the rate. */
  lookupsPerSession: number | null;
};

/** One row per document, using the same look-up rate the leaderboard and the
 *  detail endpoint both use, so the three cannot disagree.
 *
 *  Unmeasurable rows sort last rather than being dropped: "this document has
 *  the term and no visit opened it" is information. */
export function lookupsByDoc(history: TermHistoryPoint[]): DocLookupRow[] {
  const byDoc = new Map<string, TermHistoryPoint[]>();
  for (const point of history) {
    byDoc.set(point.doc_id, [...(byDoc.get(point.doc_id) ?? []), point]);
  }
  return [...byDoc.entries()]
    .map(([doc_id, points]) => {
      const hovers = points.reduce((sum, p) => sum + p.hover_count, 0);
      const sessions = points.reduce((sum, p) => sum + p.unique_sessions, 0);
      return {
        doc_id,
        hovers,
        sessions,
        quickExits: points.reduce((sum, p) => sum + p.abandonment_within_5s, 0),
        medianDwellMs: medianOf(points.map((p) => p.median_dwell_ms)),
        sectionShare: medianOf(points.map((p) => p.share_of_section_views)),
        lookupsPerSession: lookupsPerSession(hovers, sessions),
      };
    })
    .sort(
      (a, b) =>
        compareByLookups(a.lookupsPerSession, b.lookupsPerSession) ||
        a.doc_id.localeCompare(b.doc_id),
    );
}

export type TermDetailScreenProps = {
  orgSlug: string;
  termId: string;
  docId: string | undefined;
  days: number;
};

export function TermDetailScreen({
  orgSlug,
  termId,
  docId,
  days,
}: TermDetailScreenProps) {
  const query = useTermDetail(orgSlug, termId, docId, days);

  if (query.isError) {
    return (
      <ErrorState
        headline="This term could not be loaded"
        detail="The dashboard could not reach the analytics service."
      />
    );
  }
  if (query.isPending) {
    return (
      <Card header="Term">
        <SkeletonStack label="Loading term detail">
          <Skeleton width={280} height={28} />
          <Skeleton shape="block" />
          <Skeleton width={452} />
        </SkeletonStack>
      </Card>
    );
  }
  if (query.data === null) {
    return (
      <EmptyState
        icon="search"
        headline="We have no term by that id"
        body={`Nothing in ${orgSlug} has been measured under the id "${termId}". It may belong to another organisation, or it may have been re-minted by an edit — editing a definition mints a new term id.`}
      />
    );
  }

  return <TermDetailBody detail={query.data} days={days} />;
}

function TermDetailBody({ detail, days }: { detail: TermDetail; days: number }) {
  const perDoc = lookupsByDoc(detail.history);
  const maxRate = perDoc[0]?.lookupsPerSession ?? 0;
  const primaryDoc = perDoc[0]?.doc_id ?? detail.doc_ids[0];

  return (
    <>
      <PageHeader
        eyebrow="Term"
        title={detail.term ?? detail.term_id}
        description={`Tracked in ${detail.doc_ids.length} ${detail.doc_ids.length === 1 ? "document" : "documents"} over the last ${days} days.`}
        actions={
          primaryDoc ? (
            <div className="maisight-analytics-header-actions">
              <Link
                className="type-labelSm"
                to="/docs/$docId"
                params={{ docId: primaryDoc }}
              >
                View in doc
              </Link>
              <Link
                className="type-labelSm"
                to="/docs/$docId/review"
                params={{ docId: primaryDoc }}
              >
                Edit definition
              </Link>
            </div>
          ) : undefined
        }
      />

      <StatRow>
        <StatTile
          label="Hovers"
          value={formatCount(detail.hover_count)}
          detail={`in the last ${days} days`}
        />
        <StatTile
          label="Median dwell"
          value={formatDuration(detail.median_dwell_ms)}
          detail="per hover on this term"
        />
        <StatTile
          label="Quick exits"
          value={formatCount(detail.abandonment_within_5s)}
          detail="left the section within 5s"
        />
        <StatTile
          label="Look-ups per visit"
          value={formatScore(detail.lookups_per_session)}
          detail={
            detail.lookups_per_session === null
              ? NO_LOOKUPS_MEASURED
              : "1.0 means opened once per visit"
          }
        />
      </StatRow>

      <Card header="Definition">
        <div className="maisight-analytics-definition">
          <div className="maisight-analytics-definition-head">
            <span className="maisight-analytics-term type-numSm">
              {detail.term ?? detail.term_id}
            </span>
            <span className="type-numSm maisight-analytics-muted">
              {detail.term_id}
            </span>
            {isOrigin(detail.origin) ? <OriginPill origin={detail.origin} /> : null}
          </div>
          <p className="maisight-analytics-quote type-body">
            {detail.definition ??
              "This term has no published definition yet — it is measured, but readers are not being told what it means."}
          </p>
          <p className="maisight-analytics-attribution type-bodyS">
            {isOrigin(detail.origin)
              ? disclose(detail.origin).footerLabel
              : "origin not recorded"}
          </p>
        </div>
      </Card>

      <div className="maisight-analytics-halves">
        <Card
          header="Where it is looked up most"
          footer={
            <span className="type-bodyS">
              {shownOf(Math.min(perDoc.length, MAX_DOC_ROWS), perDoc.length, "document")}
            </span>
          }
        >
          <ul className="maisight-analytics-meters" aria-label="Where this term is looked up most">
            {perDoc.slice(0, MAX_DOC_ROWS).map((row) => (
              <li key={row.doc_id} className="maisight-analytics-meter-row">
                <div className="maisight-analytics-meter-head">
                  <span className="maisight-analytics-term type-numSm">{row.doc_id}</span>
                  <span
                    className="type-numSm maisight-analytics-muted maisight-analytics-metric"
                    data-measured={row.lookupsPerSession === null ? "no" : "yes"}
                    title={row.lookupsPerSession === null ? NO_LOOKUPS_WHY : LOOKUPS_PER_VISIT_WHY}
                  >
                    {formatLookupRate(row.lookupsPerSession)}
                  </span>
                </div>
                {/* No bar where there is no rate. A meter at zero would be a
                  * confident claim about a document nobody visited. */}
                {row.lookupsPerSession === null ? null : (
                  <MeterBar
                    value={row.lookupsPerSession}
                    max={maxRate}
                    tone="danger"
                    label={`${row.doc_id}: ${formatScore(row.lookupsPerSession)} look-ups per visit`}
                  />
                )}
                <p className="maisight-analytics-meter-meta type-bodyS">
                  <span>{`${formatCount(row.hovers)} look-ups · ${formatCount(row.sessions)} visits · ${formatCount(row.quickExits)} quick exits · `}</span>
                  <span
                    className="maisight-analytics-metric"
                    title={row.sectionShare === null ? NOT_MEASURED_WHY : undefined}
                    data-measured={row.sectionShare === null ? "no" : "yes"}
                  >
                    {row.sectionShare === null
                      ? `section share ${NOT_MEASURED}`
                      : `${formatShare(row.sectionShare)} ${SECTION_SHARE_LABEL}`}
                  </span>
                </p>
              </li>
            ))}
          </ul>
        </Card>

        <Card header="Impact radius">
          <p className="type-body">
            {`Changing this definition affects ${detail.doc_ids.length} ${detail.doc_ids.length === 1 ? "document" : "documents"}.`}
          </p>
          <div className="maisight-analytics-chips">
            {detail.doc_ids.slice(0, MAX_DOC_CHIPS).map((docId) => (
              <Badge key={docId}>{docId}</Badge>
            ))}
            {detail.doc_ids.length > MAX_DOC_CHIPS ? (
              <Badge>{`+${detail.doc_ids.length - MAX_DOC_CHIPS} more`}</Badge>
            ) : null}
          </div>
        </Card>
      </div>
    </>
  );
}
